"use client"

import type { AllPostsQueryResult } from "@/sanity.types"
import { Categories } from "blog/components/Categories"
import { LargeCard } from "blog/components/LargeCard"
import { PostList } from "blog/components/PostList"
import { SearchBar } from "blog/components/SearchBar"
import WarningL from "images/Icons/WarningL.inline.svg"
import Chevron from "images/blog/chevron.inline.svg"
import { css, fmobile, fresponsive, ftablet, styled } from "library/styled"
import { useMedia } from "library/useMedia"
import { useCallback, useMemo, useState } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"

export function BlogHomeClient({
	allCards,
	externalPosts,
}: {
	allCards: AllPostsQueryResult
	externalPosts: AllPostsQueryResult
}) {
	const [searchValue, setSearchValueAction] = useState("")
	const [filterWords, setFilterWordsAction] = useState<string[]>([])
	const [lastUpdated, setLastUpdatedAction] = useState<
		"searchValue" | "filterWords"
	>("searchValue")

	const featuredCard = allCards.find((x) => x?.isFeatured)
	const smallPostListAmount = useMedia(9, 9, 4, 2)
	const inTheNewAmount = useMedia(3, 3, 2, 2)

	const searchFilter = useCallback(
		(posts: AllPostsQueryResult, searchValue: string) => {
			if (!searchValue) return posts
			const searchLower = searchValue.toLowerCase()
			return posts.filter(
				(post) =>
					post?.title?.toLowerCase().includes(searchLower) ||
					post?.author?.toLowerCase().includes(searchLower) ||
					post?.preview?.toLowerCase().includes(searchLower) ||
					post?.categories?.some((cat) =>
						cat?.toLowerCase().includes(searchLower),
					),
			)
		},
		[],
	)

	const categoryFilter = useCallback(
		(posts: AllPostsQueryResult, filterWords: string[]) => {
			if (!filterWords.length) return posts
			return posts.filter((post) =>
				post?.categories?.some((category) => filterWords.includes(category)),
			)
		},
		[],
	)

	const filteredPosts = useMemo(() => {
		if (searchValue && lastUpdated === "searchValue") {
			return searchFilter(allCards, searchValue)
		}

		if (filterWords.length && lastUpdated === "filterWords") {
			return categoryFilter(allCards, filterWords)
		}

		return allCards
	}, [
		allCards,
		searchValue,
		filterWords,
		lastUpdated,
		categoryFilter,
		searchFilter,
	])

	const handleSearchChange = (value: string) => {
		setSearchValueAction(value)
		setLastUpdatedAction("searchValue")

		if (value && lastUpdated !== "searchValue") {
			setFilterWordsAction([])
		}
	}

	const showFilteredView = searchValue || filterWords.length > 0

	return (
		<Container>
			<SearchBar value={searchValue} action={handleSearchChange} />
			<Categories
				items={allCards}
				filterWords={filterWords}
				setFilterWordsAction={setFilterWordsAction}
				setLastUpdatedAction={setLastUpdatedAction}
				lastUpdated={lastUpdated}
			/>

			{!showFilteredView ? (
				<>
					{featuredCard && <LargeCard post={featuredCard} />}
					<SectionHeading>All Articles</SectionHeading>
					<PostList posts={allCards} resultsPerPage={smallPostListAmount} />
				</>
			) : (
				<>
					<FilteredSection>
						<SectionHeadingFilter>
							<Green>All Articles</Green> <Chevron />{" "}
							{searchValue ? "Search" : "Filter"}
						</SectionHeadingFilter>
						{filteredPosts.length > 0 ? (
							<PostList
								posts={filteredPosts}
								resultsPerPage={smallPostListAmount}
							/>
						) : (
							<NoArticles>
								<WarningL />
								no results found. This search is out-of-network.
							</NoArticles>
						)}
					</FilteredSection>
				</>
			)}
			{externalPosts.length > 0 && (
				<>
					<SectionHeading>In The News</SectionHeading>
					<PostList posts={externalPosts} resultsPerPage={inTheNewAmount} />
				</>
			)}
		</Container>
	)
}

const Container = styled("div", {
	...fresponsive(css`
		display: flex;
		flex-direction: column;
		gap: 80px;
		padding: 0 32px 80px;
	`),

	...ftablet(css`
		padding: 0 32px;
	`),

	...fmobile(css`
		padding: 0 24px;
		gap: 48px;
	`),
})

const SectionHeading = styled("div", {
	...fresponsive(css`
		${textStyles.h5};
		color: ${colors.green500};
		margin: 0;
		width: 100%;
		display: flex;
		align-items: center;

		&:first-of-type {
			border-top: 1px solid ${colors.green200};
		}
	`),

	...fmobile(css`
		${textStyles.bodyXL};
	`),
})

const SectionHeadingFilter = styled(SectionHeading, {
	...fresponsive(css`
		svg {
			width: 12px;
			height: 18px;
		}

		&:first-of-type {
			border-top: none;

			svg {
				margin: 0 24px;
			}
		}
	`),

	...ftablet(css`
		svg {
			margin: 0 16px;
		}

		&:first-of-type {
			border-top: none;
		}
	`),

	...fmobile(css`
		svg {
			margin: 0 12px;
		}

		&:first-of-type {
			border-top: none;
		}
	`),
})

const FilteredSection = styled(
	"div",
	fresponsive(css`
		display: flex;
		flex-direction: column;
		gap: 64px;
	`),
)

const Green = styled(
	"span",
	fresponsive(css`
		color: ${colors.green300};
	`),
)

const NoArticles = styled("div", {
	...fresponsive(css`
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		${textStyles.titleR};
		color: ${colors.orange};
		gap: 24px;
		padding: 120px 0 200px;

		svg {
			width: 48px;
			height: 48px;
		}
	`),

	...fmobile(css`
		${textStyles.titleS};
		width: 235px;
		padding: 80px 0 136px;
		margin: 0 auto;
	`),
})
